import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Grid,
  Box,
  CircularProgress,
  InputLabel,
} from "@mui/material";
import EMPInteractiveList from "../../../../shared/components/EMPInteractiveList/EMPInteractiveList";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DeliveryClient from "../../../../clients/DeliveryClient";
import EMPDialog from "../../../../shared/components/EMPDialog/EMPDialog";
import EMPTextAreaField from "../../../../shared/components/EMPTextAreaField/EMPTextAreaField";
import EMPInputField from "../../../../shared/components/EMPInputField/EMPInputField";
import { withTranslation } from "react-i18next";
import * as AnalyticsService from "../../../../shared/services/analytics/AnalyticsService";
import EMPToggleButtons from "../../../../shared/components/EMPToggleButtons/EMPToggleButtons";
import usePreDeliveryTasks from "../../../../shared/hooks/usePreDeliveryTasks";
import { toggleButtonItems } from "../../../../shared/constants/PreDeliveryTaskConstants";
import { isLincolnDeliveryPilotUser } from "../../../../utils/lincolnDeliveryPilot";
import { IS_CANADA_MARKET } from "../../../../utils/EmpUtil";

const initialFormState = {
  taskName: "",
  taskDescription: "",
  taskUrl: "",
};

const fieldLabelSx = {
  color: "var(--color-text-onlight-moderate-default, #333)",
  fontFamily: 'var(--body-2-regular-font-family, "Lincoln Proxima Nova")',
  fontSize: "var(--body-2-regular-font-size, 14px)",
  fontStyle: "normal",
  fontWeight: "var(--body-2-regular-font-weight, 400)",
  lineHeight: "var(--body-2-regular-line-height, 20px)",
};

const characterCounterSx = {
  color: "var(--color-text-onlight-subtle, #666)",
  fontFamily: 'var(--caption-regular-font-family, "Lincoln Proxima Nova")',
  fontSize: "var(--caption-regular-font-size, 12px)",
  fontStyle: "normal",
  fontWeight: "var(--caption-regular-font-weight, 400)",
  lineHeight: "var(--caption-regular-line-height, 16px)",
};

const saveButtonSx = {
  display: "flex",
  height: "var(--button-primary-height-default, 40px)",
  minWidth: "auto",
  padding: "8px 12px 8px var(--space-md, 12px)",
  justifyContent: "center",
  alignItems: "center",
  gap: "var(--space-none, 0)",
  boxSizing: "border-box",
  borderRadius: "var(--button-primary-radius-default, 4px)",
  backgroundColor: "var(--button-primary-onlight-default, #485B65)",
  color: "#fff",
  textTransform: "none",

  "&:hover": {
    backgroundColor: "var(--button-primary-onlight-default, #485B65)",
  },
};

const cancelButtonSx = {
  display: "flex",
  height: "var(--button-primary-height-default, 40px)",
  minWidth: "auto",
  padding: "var(--space-sm, 8px) 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "var(--space-none, 0)",
  boxSizing: "border-box",
  borderRadius: "var(--button-primary-radius-default, 4px)",
  border: "1px solid var(--color-stroke-onlight-interactive, #485B65)",
  backgroundColor: "transparent",
  color: "var(--color-stroke-onlight-interactive, #485B65)",
  textTransform: "none",

  "&:hover": {
    backgroundColor: "transparent",
    border: "1px solid var(--color-stroke-onlight-interactive, #485B65)",
  },
};

const vehicleTypeMap = {
  evTasks: "ev",
  iceTasks: "ice",
};

const normalizeBrandCode = (brand) => {
  const normalizedBrand = String(brand || "")
    .trim()
    .toUpperCase();

  if (normalizedBrand === "F" || normalizedBrand === "FORD") {
    return "F";
  }

  if (normalizedBrand === "L" || normalizedBrand === "LINCOLN") {
    return "L";
  }

  return null;
};

const extractPaCode = (identifier) => {
  const normalizedIdentifier = String(identifier || "").trim();

  if (!normalizedIdentifier) {
    return "";
  }

  if (!normalizedIdentifier.includes("|")) {
    return normalizedIdentifier;
  }

  const [, paCode] = normalizedIdentifier.split("|");

  return paCode?.trim() || "";
};

const getDealerBrandCode = (user) => {
  return (
    normalizeBrandCode(user?.commonId?.slice(-1)) ||
    normalizeBrandCode(user?.dealerBrand) ||
    normalizeBrandCode(user?.brand)
  );
};

const getDealerPaCode = (user) => {
  const identifiers = [
    user?.paCode,
    user?.dealerId,
    user?.commonId,
    user?.commonDealerId,
  ];

  for (const identifier of identifiers) {
    const paCode = extractPaCode(identifier);

    if (paCode) {
      return paCode;
    }
  }

  return "";
};

const Loader = ({ t }) => {
  return (
    <Grid container spacing={2}>
      <Grid item>
        <CircularProgress size={24} />
      </Grid>

      <Grid item>
        <Typography variant="subtitle1" align="center">
          {t("DeliveryTasks.loadingTasks")}
        </Typography>
      </Grid>
    </Grid>
  );
};

Loader.propTypes = {
  t: PropTypes.func.isRequired,
};

const ConfirmationDialog = ({
  open,
  dialogTitle,
  handleClose,
  id,
  user,
  dealerPaCode,
  t,
  updateTasks,
  vehicleType,
}) => {
  const queryClient = useQueryClient();

  const deleteTask = useMutation({
    mutationFn: (taskId) =>
      DeliveryClient.removePreDeliveryTask(
        dealerPaCode,
        {
          id: taskId,
        },
        user?.commonId
      ),

    onSuccess: ({ data }) => {
      handleClose();

      queryClient.setQueryData(
        ["preDeliveryTasks", dealerPaCode, user?.commonId],
        data
      );

      updateTasks(data);

      AnalyticsService.trackOnClickEvent(
        `fv:emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}`,
        `emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}:content:save`,
        `fv:emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}:content action`,
        user
      );
    },
  });

  const trackOpen = () => {
    AnalyticsService.trackPageLoadEvent(
      `fv:emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}`,
      user
    );
  };

  const secondaryAction = () => {
    handleClose();

    AnalyticsService.trackOnClickEvent(
      `fv:emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}`,
      `emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}:content:cancel`,
      `fv:emp:delivery tools:pre-delivery tasks:remove a task popup:${vehicleTypeMap[vehicleType]}:content action`,
      user
    );
  };

  const checkToDisableRemoveButton = () => {
    return (
      !dealerPaCode ||
      deleteTask.isPending ||
      deleteTask.isError ||
      !user.permissions?.rolePermissions?.hasOwnProperty(
        "preDeliveryTasksSubmit"
      )
    );
  };

  return (
    <EMPDialog
      open={open}
      dialogTitle={dialogTitle}
      hasCloseIcon
      handleClose={handleClose}
      hasActionButton
      actionButtonText={t("DeliveryTasks.removeTask")}
      onConfirm={() => deleteTask.mutate(id)}
      actionLoading={deleteTask.isPending}
      disableActionButton={checkToDisableRemoveButton()}
      hasSecondaryActionButton
      secondaryActionButtonText={t("common.cancel")}
      onSecondaryAction={secondaryAction}
      onEnter={trackOpen}
    >
      <Box mb={2}>
        <Typography variant="body1">
          {t("DeliveryTasks.deleteConfirmationText")}
        </Typography>
      </Box>

      {deleteTask.isError && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" color="error">
            {`${t("common.error")}: ${deleteTask.error.message}`}
            <br />
            {t("DeliveryTasks.taskCouldNotBeRemoved")}
          </Typography>
        </Grid>
      )}
    </EMPDialog>
  );
};

ConfirmationDialog.propTypes = {
  dealerPaCode: PropTypes.string,
  dialogTitle: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  updateTasks: PropTypes.func.isRequired,
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object,
    }),
    token: PropTypes.string,
  }).isRequired,
  vehicleType: PropTypes.oneOf(["evTasks", "iceTasks"]).isRequired,
};

const TasksList = ({
  tasks,
  actionOnClick,
  primaryActionOnClick,
  secondaryActionOnClick,
  t,
  appear,
  isLincolnPilot,
}) => {
  tasks?.sort((a, b) => b.id - a.id);

  const requiredTasks = tasks?.filter(
    (task) => task.isRequired && task.isDefault
  );

  const optionalTasks = tasks?.filter(
    (task) => !task.isRequired && task.isDefault
  );

  const userAddedTasks = tasks?.filter(
    (task) => !task.isRequired && !task.isDefault
  );

  return (
    <Box>
      <Box>
        <EMPInteractiveList
          testId="dealer-added-list"
          hasListAction
          listActionText={
            isLincolnPilot
              ? t("DeliveryTasks.createNewDeliveryChecklistTask")
              : "Add dealer task"
          }
          listActionOnClick={actionOnClick}
          items={userAddedTasks?.length > 0 && userAddedTasks}
          hasActions
          {...(!isLincolnPilot
            ? {
                primaryActionText: t("DeliveryTasks.editTask"),
                primaryActionOnClick,
              }
            : {})}
          secondaryActionText={t("DeliveryTasks.removeTask")}
          secondaryActionOnClick={secondaryActionOnClick}
          appear={appear}
        />
      </Box>

      {requiredTasks && requiredTasks.length > 0 && (
        <>
          <Box mt={5} mb={2}>
            <Typography variant="body1">
              {t("DeliveryTasks.requiredTasks")}
            </Typography>
          </Box>

          <Box>
            <EMPInteractiveList
              items={requiredTasks}
              testId="required-default-list"
              disableAppear
            />
          </Box>
        </>
      )}

      {optionalTasks && optionalTasks.length > 0 && (
        <>
          <Box mt={5} mb={2}>
            <Typography variant="body1">
              {t("DeliveryTasks.optionalTasks")}
            </Typography>
          </Box>

          <Box>
            <EMPInteractiveList
              items={optionalTasks}
              testId="optional-default-list"
              appear={appear}
              hasActions
              secondaryActionText={t("DeliveryTasks.removeTask")}
              secondaryActionOnClick={secondaryActionOnClick}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

TasksList.propTypes = {
  actionOnClick: PropTypes.func.isRequired,
  appear: PropTypes.bool,
  isLincolnPilot: PropTypes.bool,
  primaryActionOnClick: PropTypes.func.isRequired,
  secondaryActionOnClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      isDefault: PropTypes.bool,
      isRequired: PropTypes.bool,
      taskDescription: PropTypes.string,
      taskName: PropTypes.string,
      taskUrl: PropTypes.string,
    })
  ),
};

const TaskEditor = ({
  user,
  dealerPaCode,
  dealerBrandCode,
  open,
  handleClose,
  action,
  formData,
  t,
  vehicleType,
  updateTasks,
}) => {
  const [payload, setPayload] = useState({
    ...initialFormState,
  });

  const queryClient = useQueryClient();

  function hasKeys(obj) {
    return Object.keys(obj).length > 0;
  }

  useEffect(() => {
    if (action === "edit" && hasKeys(formData)) {
      setPayload(formData);
    }
  }, [formData, action]);

  const trackOpenAndClearForm = () => {
    AnalyticsService.trackPageLoadEvent(
      `fv:emp:delivery tools:pre-delivery tasks:${action} a task popup:${vehicleTypeMap[vehicleType]}`,
      user
    );
  };

  const confirmAction = (taskPayload) => {
    const mappedVehicleType = vehicleTypeMap[vehicleType];

    const updatedPayload = {
      vehicleType: mappedVehicleType,
      ...taskPayload,
      brandCode: normalizeBrandCode(taskPayload.brandCode) || dealerBrandCode,
    };

    if (action === "edit") {
      updateTaskMutation.mutate(updatedPayload);
    } else {
      addTaskMutation.mutate(updatedPayload);
    }

    AnalyticsService.trackOnClickEvent(
      `fv:emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}`,
      `emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}:content:save`,
      `fv:emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}:content action`,
      user
    );
  };

  const secondaryAction = () => {
    const mappedVehicleType = vehicleTypeMap[vehicleType];

    handleClose();

    AnalyticsService.trackOnClickEvent(
      `fv:emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}`,
      `emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}:content:cancel`,
      `fv:emp:delivery tools:pre-delivery tasks:${action} a task popup:${mappedVehicleType}:content action`,
      user
    );
  };

  const addTaskMutation = useMutation({
    mutationFn: (taskPayload) =>
      DeliveryClient.addPreDeliveryTask(
        dealerPaCode,
        taskPayload,
        user?.commonId
      ),
    retry: 0,

    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        ["preDeliveryTasks", dealerPaCode, user?.commonId],
        data
      );

      updateTasks(data);
      handleClose();
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: (taskPayload) =>
      DeliveryClient.updatePreDeliveryTask(
        dealerPaCode,
        taskPayload,
        user?.commonId
      ),
    retry: 1,

    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        ["preDeliveryTasks", dealerPaCode, user?.commonId],
        data
      );

      updateTasks(data);
      handleClose();
    },
  });

  const checkToDisableAddOrEditButton = () => {
    return (
      !dealerPaCode ||
      !hasTaskDetails(payload) ||
      addTaskMutation.isPending ||
      updateTaskMutation.isPending ||
      !user.permissions?.rolePermissions?.hasOwnProperty(
        "preDeliveryTasksSubmit"
      )
    );
  };

  const collectFormData = (fieldKey, value) => {
    setPayload((previousState) => ({
      ...previousState,
      [fieldKey]: value,
    }));
  };

  const isValidUrlOrEmpty = (url) => {
    const urlRegex = /^(http|https):\/\//i;

    if (url === "") {
      return true;
    }

    return urlRegex.test(url);
  };

  const hasTaskDetails = (task) =>
    task.hasOwnProperty("taskName") &&
    task.taskName !== "" &&
    task.hasOwnProperty("taskDescription") &&
    task.taskDescription !== "" &&
    task.hasOwnProperty("taskUrl") &&
    isValidUrlOrEmpty(task.taskUrl);

  return (
    <EMPDialog
      dialogTitle={
        action === "edit"
          ? "Edit dealer-created vehicle task"
          : "Add new dealer-created vehicle task"
      }
      dialogTitleSx={{
        paddingTop: "24px",
      }}
      dialogHorizontalPadding="56px"
      dialogActionsBottomPadding="32px"
      open={open}
      hasCloseIcon
      handleClose={handleClose}
      onEnter={trackOpenAndClearForm}
      onExit={() => {
        addTaskMutation.reset();
        updateTaskMutation.reset();
        setPayload(initialFormState);
      }}
      hasActionButton
      actionButtonText="Save"
      actionButtonSx={saveButtonSx}
      onConfirm={() => confirmAction(payload)}
      hasSecondaryActionButton
      secondaryActionButtonText="Cancel"
      secondaryActionButtonSx={cancelButtonSx}
      onSecondaryAction={secondaryAction}
      actionLoading={addTaskMutation.isPending || updateTaskMutation.isPending}
      disableActionButton={checkToDisableAddOrEditButton()}
    >
      <Box mb={3}>
        <Typography variant="body1">
          You can add your own vehicle task to appear in the Delivery Day Tool
          here.
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12} sm={8}>
          <Box mb={2}>
            <EMPTextAreaField
              testId="task-name"
              label={t("DeliveryTasks.formLabels.taskName")}
              labelSx={fieldLabelSx}
              characterCounterSx={characterCounterSx}
              required
              hasCharacterCount
              maxCharacters={50}
              minRows={2}
              value={payload.taskName}
              onChange={(event) =>
                collectFormData("taskName", event.target.value)
              }
            />
          </Box>

          <Box mb={2}>
            <EMPTextAreaField
              testId="task-description"
              label={t("DeliveryTasks.formLabels.taskDetails")}
              labelSx={fieldLabelSx}
              characterCounterSx={characterCounterSx}
              required
              hasCharacterCount
              maxCharacters={50}
              minRows={3}
              value={payload.taskDescription}
              onChange={(event) =>
                collectFormData("taskDescription", event.target.value)
              }
            />
          </Box>

          <Box mb={2}>
            <InputLabel
              disableAnimation
              shrink={false}
              sx={{
                mb: 1,
                ...fieldLabelSx,
              }}
            >
              {t("DeliveryTasks.formLabels.additionalInfo")}
            </InputLabel>

            <EMPInputField
              testId="task-url"
              value={payload.taskUrl}
              helperText={
                isValidUrlOrEmpty(payload.taskUrl)
                  ? t("DeliveryTasks.helperText2")
                  : t("DeliveryTasks.helperText1")
              }
              onChange={(event) =>
                collectFormData("taskUrl", event.target.value)
              }
              error={!isValidUrlOrEmpty(payload.taskUrl)}
            />
          </Box>
        </Grid>

        {addTaskMutation.isError && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="error">
              {`${t("common.error")}: ${addTaskMutation.error.message}`}
              <br />
              {t("DeliveryTasks.taskCouldNotBeSaved")}
            </Typography>
          </Grid>
        )}

        {updateTaskMutation.isError && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="error">
              {`${t("common.error")}: ${updateTaskMutation.error.message}`}
              <br />
              {t("DeliveryTasks.taskCouldNotBeEdited")}
            </Typography>
          </Grid>
        )}
      </Grid>
    </EMPDialog>
  );
};

TaskEditor.propTypes = {
  action: PropTypes.oneOf(["add", "edit"]).isRequired,
  dealerBrandCode: PropTypes.oneOf(["F", "L"]),
  dealerPaCode: PropTypes.string,
  formData: PropTypes.shape({
    brandCode: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskDescription: PropTypes.string,
    taskName: PropTypes.string,
    taskUrl: PropTypes.string,
  }),
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  updateTasks: PropTypes.func.isRequired,
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object,
    }),
    token: PropTypes.string,
  }).isRequired,
  vehicleType: PropTypes.oneOf(["evTasks", "iceTasks"]).isRequired,
};

const PreDeliveryTasks = ({ user, t, i18n }) => {
  const dealerPaCode = getDealerPaCode(user);
  const isLincolnPilot = isLincolnDeliveryPilotUser(user);
  const dealerBrandCode = getDealerBrandCode(user);
  const isLincolnDealer = dealerBrandCode === "L" || isLincolnPilot;
  const useFrenchTasks =
    IS_CANADA_MARKET(user?.market) &&
    i18n?.language?.toLowerCase().startsWith("fr");

  const [vehicleType, setVehicleType] = useState("evTasks");
  const [isApiUpdate, setIsApiUpdate] = useState(false);

  const selectedVehicleType = isLincolnDealer ? "iceTasks" : vehicleType;

  const { tasks, filteredTasks, loading, error, filterTasks, updateTasks } =
    usePreDeliveryTasks(selectedVehicleType, dealerPaCode, user?.commonId);

  const tasksForSelectedType =
    tasks?.[selectedVehicleType] ?? filteredTasks ?? [];

  const brandFilteredTasks = tasksForSelectedType.filter((task) => {
    if (!task.isDefault) {
      return true;
    }

    if (!IS_CANADA_MARKET(user?.market) || dealerBrandCode !== "F") {
      return false;
    }

    return normalizeBrandCode(task.brandCode) === dealerBrandCode;
  });
  const localizedTasks = useFrenchTasks
    ? brandFilteredTasks.map((task) => ({
        ...task,
        taskName: task.secondaryTaskName || task.taskName,
        taskDescription: task.secondaryTaskDescription || task.taskDescription,
      }))
    : brandFilteredTasks;

  const [editorOpen, setEditorOpen] = useState({
    open: false,
    action: "",
    payload: {},
  });

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState({
    open: false,
    id: null,
    vehicleType: "",
  });

  const handleVehicleTypeChange = (value) => {
    setIsApiUpdate(false);
    setVehicleType(value);

    setTimeout(() => filterTasks(value), 100);
  };

  const handleTasksUpdate = (newData, currentVehicleType) => {
    setIsApiUpdate(true);
    updateTasks(newData, currentVehicleType);
  };

  useEffect(() => {
    const mappedVehicleType = vehicleTypeMap[selectedVehicleType];

    AnalyticsService.trackPageLoadEvent(
      `fv:emp:delivery tools:pre-delivery tasks:${mappedVehicleType}`,
      user
    );
  }, [user, selectedVehicleType]);

  let content;

  if (loading) {
    content = <Loader t={t} />;
  } else if (error) {
    content = (
      <Typography variant="subtitle1" color="error">
        {t("common.error")}
        <br />
        {t("DeliveryTasks.listFailedToLoad")}
      </Typography>
    );
  } else {
    content = (
      <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={4}>
        {!isLincolnDealer && (
          <Box
            gridColumn={{
              xs: "span 12",
              sm: "span 6",
              md: "span 4",
            }}
          >
            <Box mb={4}>
              <EMPToggleButtons
                groupHeading="Tasks for vehicle type"
                value={vehicleType}
                onChange={(value) => handleVehicleTypeChange(value)}
                items={toggleButtonItems}
              />
            </Box>
          </Box>
        )}

        <Box gridColumn="span 12" />

        <Box
          gridColumn={{
            xs: "span 12",
            sm: "span 12",
            md: "span 8",
          }}
        >
          <TasksList
            appear={isApiUpdate}
            t={t}
            tasks={localizedTasks}
            isLincolnPilot={isLincolnPilot}
            actionOnClick={() =>
              setEditorOpen({
                open: true,
                action: "add",
                payload: {},
              })
            }
            primaryActionOnClick={(payload) =>
              setEditorOpen({
                open: true,
                action: "edit",
                payload,
              })
            }
            secondaryActionOnClick={(id) =>
              setConfirmationDialogOpen({
                open: true,
                id,
                vehicleType: selectedVehicleType,
              })
            }
          />

          <TaskEditor
            t={t}
            user={user}
            dealerPaCode={dealerPaCode}
            dealerBrandCode={dealerBrandCode}
            open={editorOpen.open}
            action={editorOpen.action}
            formData={editorOpen.payload}
            vehicleType={selectedVehicleType}
            handleClose={() =>
              setEditorOpen({
                open: false,
                action: "",
                payload: {},
              })
            }
            updateTasks={(newData) =>
              handleTasksUpdate(newData, selectedVehicleType)
            }
          />

          <ConfirmationDialog
            updateTasks={(newData) =>
              handleTasksUpdate(newData, selectedVehicleType)
            }
            t={t}
            user={user}
            dealerPaCode={dealerPaCode}
            open={confirmationDialogOpen.open}
            dialogTitle={t("DeliveryTasks.deleteConfirmationHeading")}
            id={confirmationDialogOpen.id}
            vehicleType={confirmationDialogOpen.vehicleType}
            handleClose={() =>
              setConfirmationDialogOpen({
                open: false,
                id: null,
                vehicleType: selectedVehicleType,
              })
            }
          />
        </Box>
      </Box>
    );
  }

  return (
    <section>
      <Grid container spacing={2}>
        <Grid item xs={12} container>
          <Grid item xs={12} sm={10} md={8}>
            <Box mt={2} mb={5}>
              <Typography variant="body1" sx={{ color: "#000" }}>
                {t("DeliveryTasks.pageSubHeading")}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box mb={5}>{content}</Box>
          </Grid>
        </Grid>

        <Grid item xs={12} sm="auto" md={3} lg={5} />
      </Grid>
    </section>
  );
};

PreDeliveryTasks.propTypes = {
  i18n: PropTypes.shape({
    language: PropTypes.string,
  }),
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string,
    dealerBrand: PropTypes.string,
    paCode: PropTypes.string,
    dealerId: PropTypes.string,
    commonId: PropTypes.string,
    commonDealerId: PropTypes.string,
    market: PropTypes.string,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.object,
    }),
    roles: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.object,
    ]),
    token: PropTypes.string,
  }).isRequired,
};

export default withTranslation("emp")(PreDeliveryTasks);
